<script setup lang="ts">
import type { NovaCreatorProfileCardProps } from './NovaCreatorProfileCard.types'
import { ROUTES } from '@configs'
const props = defineProps<NovaCreatorProfileCardProps>()
const { t } = useI18n()
const memberStatus = computed(() => {
  switch (props.creator.creatorDaoSbscrbSttusCode) {
    case 'P':
      return t('dao.createCard.sttusCode.pCode')
    case 'S':
      return t('dao.createCard.sttusCode.sCode')
    case 'T':
    case 'R':
    case 'B':
    default:
      return ''
  }
})
</script>

<template>
  <NuxtLink
    :class="[
      'nova-creator-profile-card',
      { 'dao-member': creator.creatorDaoOpenedAt === 'Y' },
    ]"
    :to="
      useRoutePathIdChange(ROUTES.CREATOR.path, {
        id: `@${props.creator.creatorLink}`,
      })
    "
  >
    <div class="portrait">
      <div class="dao-badge">FAM</div>
      <NovaImageContainer
        :ratio="'1:1'"
        :image-url="creator.creatorProfl || ''"
      />
      <div
        :class="[
          'dao-member-status',
          {
            'dao-join': ['P', 'S'].includes(creator.creatorDaoSbscrbSttusCode),
          },
        ]"
      >
        {{ memberStatus }}
      </div>
    </div>
    <div class="profile-info">
      <h4 class="creator-name">{{ creator.creatorNcnm || '&nbsp;' }}</h4>
      <div class="category-wrap">
        <p class="category">{{ creator.ctgryNm || '&nbsp;' }}</p>
      </div>
      <p class="description">{{ creator.proflDc || '&nbsp;' }}</p>
    </div>
  </NuxtLink>
</template>

<style scoped lang="scss">
.nova-creator-profile-card {
  display: flex;
  flex-direction: column;
  width: 100%;
  height: auto;
  border-radius: 10px;
  background-color: $color-white;
  overflow: hidden;
  box-shadow: 0 2px 4px 0 rgba(0, 0, 0, 0.08);

  @include mobile {
    border-radius: 10px;
  }

  .portrait {
    position: relative;
    background-color: $color-bg-custom-1;
    .dao-badge {
      position: absolute;
      top: 5px;
      right: 5px;
      display: none;
      color: $color-white;
      background-color: $color-blue-900;
      padding: 1px 6px;
      z-index: 10;
      border-radius: 10px;
      @include text-style($text-body-14-medium);
      @include mobile {
        @include text-style($text-body-12-medium);
      }
    }
    .dao-member-status {
      position: absolute;
      bottom: 0;
      left: 0;
      width: 100%;
      display: none;
      text-align: center;
      background: rgba(0, 0, 0, 0.5);
      color: $color-white;
      padding: 4px 4px;
      z-index: 10;
      @include text-style($text-body-14-medium);
      @include mobile {
        @include text-style($text-body-12-medium);
      }
    }
  }

  .profile-info {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding: 15px 15px 15px;

    @include mobile {
      padding: 10px 10px 10px;
    }

    .creator-name {
      color: $color-text-11;
      font-size: 20px;
      font-style: normal;
      font-weight: 700;
      line-height: 140%; /* 28px */
      letter-spacing: -0.5px;
      @include ellipsis(1);
      @include mobile {
        font-size: 15px;
        letter-spacing: -0.375px;
      }
    }

    .category-wrap {
      position: relative;
      margin-top: 3px;

      &:after {
        position: absolute;
        bottom: -3px;
        left: 50%;
        content: '';
        width: 20px;
        height: 1px;
        background-color: $color-neutral-dark-4;
        transform: translateX(-50%);
      }

      .category {
        color: $color-neutral-dark-4;
        font-size: 12px;
        font-style: normal;
        font-weight: 500;
        line-height: 140%; /* 19.6px */
        letter-spacing: -0.35px;

        @include ellipsis(1);

        @include mobile {
          font-size: 10px;
          letter-spacing: -0.25px;
        }
      }
    }

    .description {
      color: $color-neutral-dark-4;
      font-size: 12px;
      font-style: normal;
      font-weight: 400;
      line-height: 140%; /* 16.8px */
      letter-spacing: -0.3px;
      margin-top: 2px;

      @include ellipsis(1);

      @include mobile {
        font-size: 10px;
        letter-spacing: -0.25px;
      }
    }
  }
  &.dao-member {
    background-color: $color-blue-900;
    .portrait {
      .dao-badge {
        display: block;
      }
      .dao-join {
        display: block;
      }
    }
    .profile-info {
      .creator-name {
        color: $color-white;
      }
      .category {
        color: $color-gray-400;
      }
      .description {
        color: $color-gray-400;
      }
    }
  }
}
</style>
